import React, { useMemo, useState } from "react";
import { Button, Flex } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { useGetProjectStatisticsQuery } from "#graphql/supergraphHooks";
import { JIRA_PROJECT_KEY } from "../constants";
import {
  cardsContainer,
  metricsGrid,
  metricItem,
  metricItemSelected,
  cardTitle,
  cardCount,
  errorMessage,
  carouselContainer,
  leftArrowButton,
  rightArrowButton,
} from "./styles_vanilla.css";
import { WorkflowCardSkeleton } from "./WorkflowCardSkeleton";

const CARDS_PER_PAGE = 6;

type WorkflowType = {
  name: string;
  value: string;
};

type WorkflowDashboardProps = {
  onWorkTypeClick?: (workType: WorkflowType, ticketCount: number) => void;
  selectedWorkTypes?: string[];
};

export const WorkflowDashboard: React.FC<WorkflowDashboardProps> = ({
  onWorkTypeClick,
  selectedWorkTypes = [],
}) => {
  const [currentPage, setCurrentPage] = useState(0);

  const { data, loading, error } = useGetProjectStatisticsQuery({
    variables: { projectId: JIRA_PROJECT_KEY },
  });

  const workflowData = useMemo(() => {
    if (!data?.projectStatistics?.issueTypeStatistics) {
      return [];
    }

    return [...data.projectStatistics.issueTypeStatistics]
      .sort((a, b) => a.issueTypeName.localeCompare(b.issueTypeName))
      .map((stat) => ({
        id: stat.issueTypeName.toLowerCase().replace(/\s+/g, "-"),
        title: stat.issueTypeName,
        // issueTypeValue is the canonical DB identifier (WE path only).
        // Falls back to issueTypeName so legacy (Jira) path is unchanged.
        value: stat.issueTypeValue ?? stat.issueTypeName,
        count: stat.activeCount,
        subtitle: stat.issueTypeName,
      }));
  }, [data]);

  const totalPages = Math.ceil(workflowData.length / CARDS_PER_PAGE);
  const startIndex = currentPage * CARDS_PER_PAGE;
  const endIndex = startIndex + CARDS_PER_PAGE;
  const currentPageData = workflowData.slice(startIndex, endIndex);

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  if (loading) {
    return (
      <div>
        <div className={cardsContainer}>
          <div className={metricsGrid}>
            {Array.from({ length: CARDS_PER_PAGE }).map((_, index) => (
              <WorkflowCardSkeleton key={index} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className={errorMessage}>Error loading workflow data: {error.message}</div>;
  }

  return (
    <div>
      <div className={carouselContainer}>
        <div className={cardsContainer}>
          {currentPage > 0 && (
            <Button
              variant="muted"
              icon="angle-double-left"
              onClick={handlePrevPage}
              className={leftArrowButton}
            />
          )}
          <div className={metricsGrid}>
            {currentPageData.map((workflow) => {
              const isSelected = selectedWorkTypes.includes(workflow.title);
              return (
                <Flex
                  key={workflow.id}
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  gap={Sizes.SM}
                  className={isSelected ? metricItemSelected : metricItem}
                  onClick={() =>
                    onWorkTypeClick?.(
                      { name: workflow.title, value: workflow.value },
                      workflow.count,
                    )
                  }
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      onWorkTypeClick?.(
                        { name: workflow.title, value: workflow.value },
                        workflow.count,
                      );
                    }
                  }}
                >
                  <div className={cardCount}>{workflow.count.toLocaleString()}</div>
                  <div className={cardTitle}>{workflow.subtitle}</div>
                </Flex>
              );
            })}
          </div>
          {currentPage < totalPages - 1 && (
            <Button
              variant="muted"
              icon="angle-double-right"
              onClick={handleNextPage}
              className={rightArrowButton}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowDashboard;
